<template>
	<div
		class="w-full user-photos-wrap"
		:class="{ public: !canEdit }"
	>
		<div
			v-if="canEdit"
			class="mb-4"
		>
			<h2 class="pt-2 pb-4 text-2xl font-bold">
				Upload Photo
			</h2>
			<form
				v-require-login="{callback: handleUploadPhoto}"
			>
				<WwImageUpload
					:auto-crop="true"
					:auto-crop-area=".99"
					:aspect-ratio="1/1"
					:initial-aspect-ratio="1/1"
					:should-reset="resetImageUploader"
					class="mb-2"
					@image-updated="handleUpdateImage"
				/>
				<input
					v-model="image"
					type="hidden"
				>
				<button
					class="px-3 py-2 font-bold text-white uppercase bg-green-500 rounded-lg"
					type="submit"
					:disabled="!readyToUpload"
				>
					Upload
				</button>
			</form>
		</div>
		<h2 class="pt-2 pb-4 text-2xl font-bold">
			Photos
		</h2>
		<div v-if="!hasPhotos">
			<EmptyList
				image-src="/img/no-photos.png"
				copy="This user has no photos."
			/>
		</div>
		<div
			v-else
			class="-mx-4"
		>
			<div
				v-infinite-scroll="{handleScroll: handleScroll, disabled: stopScroll}"
				class="img-item"
				:style="{'--perPage': perPage}"
			>
				<draggable
					v-model="photos.ids"
					class="list-group"
					tag="ul"
					v-bind="dragOptions"
					:disabled="!canEdit"
					@start="drag = true"
					@end="dragend()"
				>
					<TransitionGroup
						class="grid grid-cols-1 gap-3 px-3 md:grid-cols-2 lg:grid-cols-3"
						appear
						name="fade-photo !drag ? 'flip-list' : null"
						:style="{'--page': page}"
					>
						<UserPhotoCard
							v-for="(photo, index) in loadedPhotos"
							:id="'photo-' + index"
							:key="`${index}-photo`"
							:photo="photo"
							:photo-id="index"
							:style="{'--index': index}"
							class="p-3 bg-white cursor-pointer"
							@emit-open-modal="openModal(USER_PHOTO_MODAL, index + 1)"
						/>
					</TransitionGroup>
				</draggable>
			</div>
			<PhotoCarouselModal
				:modal-id="USER_PHOTO_MODAL"
				title="Gallery"
				:active-slide="modalPhotoId"
				:slides="slides"
				:current-index="modalPhotoId"
			/>
		</div>
	</div>
</template>

<script>
import draggable from 'vuedraggable'
import {
	mapActions,
	mapGetters,
	mapMutations
} from 'vuex'

import destructureUserImage from '@/api/helpers/destructureUserImage.js'
import EmptyList from '@/components/multiUse/EmptyList'
import WwLazyLoader from '@/components/UI/WwLazyLoader.vue'
import {
	USER_PHOTO_MODAL
} from '@/constants/modal/names.js'
import {
	ERROR,
	SUCCESS
} from '@/constants/toast/type'
import { logError } from '@/utils/error-handling.js'

const WwImageUpload = () => ({
	component: import('@/components/UI/WwImageUpload.vue'),
	loading: WwLazyLoader
})

export default {
	components: {
		draggable,
		EmptyList,
		PhotoCarouselModal: () => import('@/components/UI/modals/PhotoCarouselModal'),
		UserPhotoCard: () => import('./components/PhotoCard'),
		WwImageUpload
	},
	data() {
		return {
			resetImageUploader: false,
			isMounted: false,
			image: '',
			customImage: {},
			photosCapped: [],
			page: 1,
			perPage: 15,
			stopScroll: false,
			hover: false,
			drag: false,
			readyToUpload: false,
			modalPhotoId: 0,
			removePickerImage: false,
			USER_PHOTO_MODAL
		}
	},
	computed: {
		hasPhotos() { return this.photos.ids && this.photos.ids.length > 0	},
		totalPages() { return this.hasPhotos && Math.ceil(this.photos.ids.length / this.perPage) },
		slides() {
			const slideArray = []
			for (let i = 0; i < this.photos.ids.length; i++) {
				const photoId = this.photos.ids[i]
				slideArray.push(destructureUserImage(this.photos.items[photoId]))
			}
			return slideArray
		},
		...mapGetters('user', [
			'photos',
			'userId',
			'userName'
		]),
		...mapGetters('auth', [
			'auth'
		]),
		bottomPhotoId() {
			return this.loadedPhotos.length - 1
		},
		loadedPhotos() {
			const tempPhotosCapped = []
			let tempPerPage = 0

			if (this.hasPhotos) {
				for (let pageNumber = 0; pageNumber < this.page; pageNumber++) { // this loops through each page number that have been scrolled to so far
					if (pageNumber == 0) { // allows the first page to start at the first photo
						tempPerPage = 1
					} else { // makes pages 2 onwards start where photo id == pageNumber * 15
						tempPerPage = this.perPage
					}
					for (let index = ((pageNumber) * tempPerPage); index < (this.perPage * (pageNumber + 1)); index++) {
						const photoIds = this.photos.ids[index]
						const photoItems = this.photos.items[photoIds]
						if (this.photos.items.length == 0) {
							continue
						}
						if (photoItems == undefined) {
							break
						} else {
							tempPhotosCapped.push(photoItems)
						}
					}
				}
			}
			return tempPhotosCapped
		},
		uploadData() {
			return {
				user_id: this.userId,
				main: this.main,
				image: this.image
			}
		},
		main() {
			return false
		},
		newPhotoOrder() {
			return this.photos.ids
		},
		dragOptions() {
			return {
				animation: 200,
				group: 'description',
				disabled: false,
				ghostClass: 'ghost'
			}
		},
		reorderData() {
			return {
				user_id: this.userId,
				images: this.newPhotoOrder
			}
		},
		canEdit () {
			return this.auth.loggedIn && this.auth.id === this.userId
		}
	},
	watch: {
		totalPages() { // this decrements page if a first image of a page gets deleted
			if (this.hasPhotos) {
				if (this.totalPages < this.page) {
					this.page = this.totalPages
				}
			}
		}
	},
	mounted() {
		this.isMounted = true
	},
	methods: {
		...mapActions('user', [
			'uploadPhoto', 'savePhotoOrder'
		]),
		...mapMutations('modal', [ 'showModal' ]),
		...mapMutations('toast', [ 'showToast' ]),
		handleUpdateImage(image) {
			this.image = image
			this.readyToUpload = true
		},
		openModal(modalId, photoId) {
			this.modalPhotoId = photoId
			this.showModal(modalId)
		},
		showErrorToast(error) {
			let errorMessage = 'Error'
			if (error.msg == 'File type not allowed.' && error.file_type !== '') {
				errorMessage = 'File type ' + "'" + error.file_type + "'" + ' not allowed.'
			} else if (error.msg == 'null' || error.msg == '') {
				errorMessage = 'Error'
			}
			this.showToast({
				primaryText: 'Upload failed',
				secondaryText: errorMessage,
				type: ERROR
			})
		},
		async handleUploadPhoto() {
			const currentTotalPages = this.totalPages // set to preserve original total to compare against new value after api call
			try {
				const { data } = await this.uploadPhoto(this.uploadData)
				if (data.success == 1) {
					this.showToast({
						primaryText: 'Success',
						secondaryText: 'Photo uploaded',
						type: SUCCESS
					})
					this.readyToUpload = false // resets our readyToUpload boolean to disable the upload button until new image is selected and ready
					this.removePickerImage = true // sends truthy value to ImageUpload.vue that triggers the croppa.remove() function to clear the image picker

					if (!this.stopScroll && currentTotalPages < this.totalPages && this.totalPages == 2 && this.page == 1) { // notes below
						/*
						*NOTE [Bo] 9/30/2020:
							Logic:
							if you are not at the bottom of the scroll window
							AND totalPages increased upon uploading this photo
							AND there are only 2 total pages
							AND the current page scrolled to is the first page

							Reason:
							this if statement will add the newly uploaded photo to the grid if not scrolled to the bottom of the first page.
							it just looks cleaner to add immediately since the grid can have an empty space (before even scrolling down) if there are less than 16 (4x4) photos.
							otherwise you'll have to scroll to the bottom of the grid to trigger the next page to see the photo get added.
						*/
						this.page++ // increment to the next page to add the new photo
					}
				}
			} catch (e) {
				logError(e)
			}
			this.resetImageUploader = true
			await this.$nextTick()
			this.resetImageUploader = false
		},
		scroll() { // looks for the last photo element, if in view, trigger inifinite scroll
			window.onscroll = () => {
				const bottomMarker = document.querySelector('#photo-' + this.bottomPhotoId)

				if (bottomMarker && this.isScrolledIntoView(bottomMarker)) {
					this.handleScroll()
				}
			}
		},
		isScrolledIntoView(el) { // check if element is in viewport
			const rect = el.getBoundingClientRect()
			const elemTop = rect.top
			const elemBottom = rect.bottom

			const isVisible = elemTop < window.innerHeight && elemBottom >= 0
			return isVisible
		},
		handleScroll() {
			if (!this.stopScroll) {
				this.stopScroll = true

				if (this.photos.ids.length > (this.perPage * this.page)) {
					this.page++
				}
			}
			this.stopScroll = false
		},
		async dragend() {
			this.drag = false
			try {
				await this.savePhotoOrder(this.reorderData)
			} catch (e) {
				logError(e)
			}
		},
		openPhotoModal(photoId) {
			this.modalPhotoId = photoId
		},
		resetModalData() {
			this.modalPhotoId = 0
		}
	}
}
</script>

<style lang="scss" scoped>
.user-photos-wrap {
	.img-item {
		text-align: center;
		img {
			text-align: center;
			width: 100%;
		}
		a {
			color: $gray1;
			.delete {
				text-align: center;
				border: 1px solid $gray4;
				border-radius: 50%;
				width: 30px;
				height: 30px;
				color: #888;
				font-size: 0.85em;
				line-height: 28px;
				margin-right: 5px;
			}
		}
		.grid-flex {
			display: flex;
			flex-wrap: wrap;
		}
	}
	.limit-height {
		max-height: 740px;
		overflow-y: auto;
		overflow-x: hidden;
	}
	.fade-photo-enter-active,
	.fade-photo-leave-active {
		//comment out next line to see slide instead of bounce
		animation: bounce-in 0.35s;
		animation-delay: calc(0.16s * var(--index) - ( 0.16s * ((var(--page) - 1) * var(--perPage)) ));
		transition: all 0.35s ease-out;
		transition-delay: calc(0.16s * var(--index) - ( 0.16s * ((var(--page) - 1) * var(--perPage)) ));
	}
	.fade-photo-enter,
	.fade-photo-leave-to {
		opacity: 0;
		transform: translateY(100px);
	}
	&.public {
		.user-photo img {
			animation: mouseOut 0.13s ease-in;
		}
		.user-photo:hover img {
			animation: mouseOver 0.1s ease-in forwards;
		}
	}
	// ANIMATIONS
	@keyframes mouseOver {
		0% {
			top: 0;
			transform: scale(1);
		}
		100% {
			top: -5px;
			transform: scale(1.05);
		}
	}

	@keyframes mouseOut {
		0% {
			top: -5px;
			transform: scale(1.05);
		}
		100% {
			top: 0;
			transform: scale(1);
		}
	}

	@keyframes bounce-in {
		0% {
			transform: scale(0);
		}
		50% {
			transform: scale(1.1);
		}
		100% {
			transform: scale(1);
		}
	}
	.img-upload {
		position: relative;
		&::before {
			position: absolute;
			left: 0;
			top: 0;
			height: 100%;
			width: 100%;
			content: "";
			background: white;
			pointer-events: none;
			border: 3px dashed $gray4;
			border-radius: 10px;
			&:hover {
				border: 3px solid red;
			}
		}
		&::after {
			position: absolute;
			left: 0;
			top: 0;
			width: 100%;
			height: 100%;
			content: "Upload an image";
			color: #888;
			font-size: 2em;
			text-align: center;
			font-weight: 600;
			padding-top: 4%;
			pointer-events: none;
		}
		input {
			border: 3px dashed $gray4;
			border-radius: 10px;
			width: 100%;
			height: 100px;
			cursor: pointer;
			&:hover {
				border: 3px dashed $primary1;
			}
		}
	}
}
</style>
